import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CharacterView, FloatingHearts, SparkleBurst, SpeakerIcon, CheckIcon } from "./fx";

// ChooseKind — the character stands in a sad/shy pose; pick a caring response.
// A kind pick brightens them to their happy pose + hearts burst, then finishes.
// A not-kind pick head-shakes, dims the card, speaks a gentle nudge, and stays
// retryable — never a "wrong!".

const TINTS = ["#ff7a8a", "#4cc9d6", "#5fce6a"];

const ChooseKind = ({ payload, sounds, onDone, prompt, speakPrompt }) => {
  const doneRef = useRef(false);
  const [chosen, setChosen] = useState(null);
  const [dimmed, setDimmed] = useState(() => new Set());
  const [shake, setShake] = useState(null);
  const [hearts, setHearts] = useState(0);
  const [burst, setBurst] = useState(0);

  const tap = useCallback(
    (choice) => {
      if (chosen) return;
      if (choice.isKind) {
        sounds.playPop(0.85);
        sounds.playSparkle();
        setChosen(choice.id);
        setHearts((h) => h + 1);
        setBurst((b) => b + 1);
        if (!doneRef.current) {
          doneRef.current = true;
          window.setTimeout(() => onDone(), 1000);
        }
      } else {
        sounds.playThud();
        setShake(`${choice.id}-${Date.now()}`);
        setDimmed((prev) => new Set(prev).add(choice.id));
        if (choice.nudge) sounds.speak(choice.nudge);
      }
    },
    [chosen, onDone, sounds]
  );

  return (
    <div className="ch-activity-body ch-choose">
      {prompt && (
        <div className="ch-prompt-capsule">
          <span>{prompt}</span>
          <button type="button" className="ch-speaker" onClick={speakPrompt} aria-label="Read aloud">
            <SpeakerIcon size={18} />
          </button>
        </div>
      )}

      {payload.character && (
        <motion.div
          className="ch-choose-character"
          animate={shake ? { x: [0, -5, 5, -4, 4, 0] } : {}}
          transition={{ duration: 0.4 }}
          key={shake || "still"}
        >
          <span className={`ch-choose-spotlight ${chosen ? "is-happy" : ""}`} aria-hidden="true" />
          <CharacterView character={payload.character} happy={!!chosen} />
          <FloatingHearts trigger={hearts} style={{ left: "50%", top: "10%" }} />
          <SparkleBurst trigger={burst} reach={100} style={{ left: "50%", top: "35%" }} />
        </motion.div>
      )}

      <div className="ch-choices">
        {payload.choices.map((choice, index) => {
          const isChosen = chosen === choice.id;
          const isDimmed = dimmed.has(choice.id);
          const tint = TINTS[index % TINTS.length];
          return (
            <motion.button
              key={choice.id}
              type="button"
              className={`ch-choice-card ${isChosen ? "is-chosen" : ""}`}
              style={{ "--ch-choice-tint": tint, opacity: isDimmed ? 0.55 : chosen && !isChosen ? 0.45 : 1 }}
              disabled={!!chosen}
              onClick={() => tap(choice)}
              initial={{ opacity: 0, y: 22, scale: 0.94 }}
              animate={
                shake && shake.startsWith(choice.id)
                  ? { opacity: 1, y: 0, scale: 1, x: [0, -8, 8, -5, 5, 0] }
                  : { opacity: 1, y: 0, scale: isChosen ? [1, 1.05, 1] : 1 }
              }
              transition={{
                duration: 0.4,
                delay: shake || chosen ? 0 : 0.15 + index * 0.1,
              }}
            >
              <span className="ch-choice-bar" />
              <span className="ch-choice-text">{choice.text}</span>
              <span className="ch-choice-icon" aria-hidden="true">
                {isChosen ? <CheckIcon size={16} /> : null}
              </span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default ChooseKind;
